import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck, faVolumeUp } from "@fortawesome/free-solid-svg-icons"
import {
	fetchNotifEligibilite,
	putNotifEligibilite,
	putNotifEligibiliteReset,
} from "../../../store/actions/notificationEligibiliteActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import Loader from "../../basics/Loader"
import CustomModal from "../../basics/interface/CustomModal"

// Onglet Configuration → Notifications : qui reçoit et traite quelle catégorie.
// Matrice catégories (lignes) × groupes (colonnes). Seuls les `groupes` sont
// modifiables — cycle de vie et voix viennent du registre backend (comportement
// de la catégorie, pas un réglage).
//
// Cocher une case = ce groupe REÇOIT (cloche + temps réel) ET CONSULTE la
// catégorie. Il n'y a plus de lecture sans case cochée (retiré le 30.07.2026).
const styles = {
	intro: {
		fontSize: 13,
		color: COLORS.textMuted,
		marginBottom: 18,
		lineHeight: 1.5,
	},
	th: { ...tableStyles.th, textAlign: "center" },
	thCat: { ...tableStyles.th, textAlign: "left", minWidth: 240 },
	// Cellule à cocher : padding 0, c'est le <label> qui remplit la case pour que
	// le clic (et le curseur) portent sur toute la case, pas sur la ligne.
	td: { ...tableStyles.td, textAlign: "center", padding: 0 },
	tdCat: { ...tableStyles.td, whiteSpace: "normal" },
	case: (survolee) => ({
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "12px 16px",
		cursor: "pointer",
		backgroundColor: survolee ? COLORS.accentLight : "transparent",
		transition: "background-color 0.12s",
	}),
	catLabel: { fontSize: 14, fontWeight: 600, color: COLORS.text },
	catMeta: {
		fontSize: 12,
		color: COLORS.textLight,
		marginTop: 3,
		display: "flex",
		alignItems: "center",
		gap: 8,
		flexWrap: "wrap",
	},
	chip: {
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		padding: "1px 8px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 500,
		backgroundColor: COLORS.bg,
		color: COLORS.textMuted,
	},
	groupe: { fontFamily: "monospace", fontSize: 11 },
	actions: { display: "flex", justifyContent: "flex-end", marginTop: 22 },
	personne: { fontSize: 12, color: COLORS.warning, marginTop: 4 },
	erreur: { color: COLORS.danger, fontSize: 13, marginTop: 10 },
}

const CYCLES = {
	traite: "à traiter (bouton Terminer)",
	auto_expire: "éphémère (expire seule)",
}

const NotificationsConfigV2 = () => {
	const dispatch = useDispatch()
	const { categories, groupes, fetchEligibilite, putEligibilite } =
		useSelector((s) => s.notifEligibiliteInfo)
	// Édition locale : { [cle]: [noms de groupes] }. Rechargée depuis le serveur
	// à chaque arrivée de données (fetch initial ET réponse du PUT).
	const [matrice, setMatrice] = useState({})
	const [modalOpen, setModalOpen] = useState(false)
	// Case survolée ({cle, groupe}) : le retour visuel est sur la CASE, pas sur
	// la ligne — c'est la case qu'on va cliquer.
	const [survol, setSurvol] = useState({})

	useEffect(() => {
		dispatch(fetchNotifEligibilite())
		return () => dispatch(putNotifEligibiliteReset())
	}, [dispatch])

	useEffect(() => {
		if (!categories.length) return
		setMatrice(
			categories.reduce(
				(acc, c) => ({ ...acc, [c.cle]: [...(c.groupes || [])] }),
				{},
			),
		)
	}, [categories])

	// Fermeture du modal après une sauvegarde réussie (feedback ✓ dans le modal).
	useEffect(() => {
		if (!putEligibilite.saved) return
		const t = setTimeout(() => {
			dispatch(putNotifEligibiliteReset())
			setModalOpen(false)
		}, 900)
		return () => clearTimeout(t)
	}, [putEligibilite.saved, dispatch])

	const toggle = (cle, groupe) =>
		setMatrice((prev) => {
			const actuels = prev[cle] || []
			return {
				...prev,
				[cle]: actuels.includes(groupe)
					? actuels.filter((g) => g !== groupe)
					: [...actuels, groupe],
			}
		})

	// Bouton actif seulement si quelque chose a changé (mêmes ensembles, ordre
	// indifférent — le serveur renvoie trié, l'édition locale non).
	const modifie = useMemo(
		() =>
			categories.some((c) => {
				const avant = [...(c.groupes || [])].sort()
				const apres = [...(matrice[c.cle] || [])].sort()
				return avant.join("|") !== apres.join("|")
			}),
		[categories, matrice],
	)

	const confirmer = () => dispatch(putNotifEligibilite(matrice))
	const fermerModal = () => {
		setModalOpen(false)
		dispatch(putNotifEligibiliteReset())
	}

	if (fetchEligibilite.loading && !categories.length)
		return <Loader variant="ring" message="Chargement des notifications…" />

	const busy = putEligibilite.saving || putEligibilite.saved

	return (
		<>
			{modalOpen && (
				<CustomModal
					title="Enregistrer les modifications ?"
					onClose={fermerModal}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Non",
							styleName: "secondary",
							onClick: fermerModal,
							disabled: busy,
						},
						{
							title: putEligibilite.saving ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : putEligibilite.saved ? (
								<FontAwesomeIcon icon={faCheck} />
							) : (
								"Oui"
							),
							styleName: "primary",
							onClick: confirmer,
							disabled: busy,
						},
					]}
				>
					Voulez-vous enregistrer qui reçoit quelles notifications ?
					{putEligibilite.error && (
						<div style={styles.erreur}>
							{putEligibilite.error.message ||
								"La sauvegarde a échoué."}
						</div>
					)}
				</CustomModal>
			)}

			<div style={styles.intro}>
				Cochez les groupes qui <strong>reçoivent et traitent</strong>{" "}
				chaque catégorie. Une catégorie sans aucun groupe coché n'est
				envoyée à personne — elle continue d'être enregistrée dans le
				journal, mais n'apparaît dans aucune cloche.
			</div>

			<div style={tableStyles.container}>
				<table style={tableStyles.table}>
					<thead>
						<tr>
							<th style={styles.thCat}>Catégorie</th>
							{groupes.map((g) => (
								<th key={g} style={styles.th}>
									<span style={styles.groupe}>{g}</span>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{categories.map((c, index) => (
							<tr
								key={c.cle}
								style={{
									backgroundColor: tableStyles.rowBg(index),
								}}
							>
								<td style={styles.tdCat}>
									<div style={styles.catLabel}>{c.label}</div>
									<div style={styles.catMeta}>
										<span style={styles.chip}>
											{CYCLES[c.cycle] || c.cycle}
										</span>
										{c.vocal && (
											<span style={styles.chip}>
												<FontAwesomeIcon
													icon={faVolumeUp}
												/>
												annonce vocale
											</span>
										)}
									</div>
									{(matrice[c.cle] || []).length === 0 && (
										<div style={styles.personne}>
											Personne ne reçoit cette catégorie.
										</div>
									)}
								</td>
								{groupes.map((g) => (
									<td key={g} style={styles.td}>
										<label
											style={styles.case(
												survol.cle === c.cle &&
													survol.groupe === g,
											)}
											onMouseEnter={() =>
												setSurvol({
													cle: c.cle,
													groupe: g,
												})
											}
											onMouseLeave={() => setSurvol({})}
										>
											<input
												type="checkbox"
												style={{ cursor: "pointer" }}
												checked={(
													matrice[c.cle] || []
												).includes(g)}
												onChange={() =>
													toggle(c.cle, g)
												}
											/>
										</label>
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div style={styles.actions}>
				<button
					type="button"
					style={modifie ? gs.btnPrimary : gs.btnDisabled}
					disabled={!modifie}
					onClick={() => setModalOpen(true)}
				>
					Enregistrer
				</button>
			</div>
		</>
	)
}

export default NotificationsConfigV2
